import {
  createProject,
  deleteProject,
  duplicateProject,
  getState,
  listProjects,
  renameProject,
  subscribe,
  switchProject,
} from '../store/project';
import type { ProjectMeta } from '../types';

export class ProjectModal {
  private bg!: HTMLElement;
  private list!: HTMLElement;
  private statusBar!: HTMLElement;

  constructor() {
    this.build();
    subscribe(() => {
      // Re-render the list whenever the active project changes (autosave bumps updatedAt).
      if (this.bg.classList.contains('show')) this.refresh();
    });
  }

  private build(): void {
    this.bg = document.createElement('div');
    this.bg.className = 'modal-bg';
    const modal = document.createElement('div');
    modal.className = 'modal modal-lg';

    const h = document.createElement('h3');
    h.textContent = '📁 프로젝트';
    const desc = document.createElement('p');
    desc.textContent =
      '브라우저 IndexedDB에 저장돼요. 같은 브라우저에서 새로고침해도 그대로. 다른 기기로 옮기려면 "내보내기".';

    const newBtn = document.createElement('button');
    newBtn.className = 'btn primary';
    newBtn.textContent = '+ 새 프로젝트';
    newBtn.style.marginTop = '12px';
    newBtn.addEventListener('click', async () => {
      const name = prompt('프로젝트 이름', '새 프로젝트');
      if (!name) return;
      await createProject(name);
      this.refresh();
      this.flash(`만들어짐: ${name}`);
    });

    this.list = document.createElement('div');
    this.list.className = 'font-list';
    this.list.style.marginTop = '12px';
    this.list.style.maxHeight = '50vh';

    this.statusBar = document.createElement('div');
    this.statusBar.style.fontSize = '11px';
    this.statusBar.style.color = 'var(--ink-dim)';
    this.statusBar.style.minHeight = '18px';
    this.statusBar.style.marginTop = '6px';

    const actions = document.createElement('div');
    actions.className = 'row-actions';
    const close = document.createElement('button');
    close.className = 'btn';
    close.textContent = '닫기';
    close.addEventListener('click', () => this.close());
    actions.appendChild(close);

    modal.append(h, desc, newBtn, this.list, this.statusBar, actions);
    this.bg.appendChild(modal);
    this.bg.addEventListener('click', (e) => {
      if (e.target === this.bg) this.close();
    });
    document.body.appendChild(this.bg);
  }

  open(): void {
    this.bg.classList.add('show');
    this.refresh();
  }
  close(): void {
    this.bg.classList.remove('show');
  }

  private async refresh(): Promise<void> {
    const projects = await listProjects();
    const currentId = getState().project.id;
    this.list.replaceChildren();
    if (projects.length === 0) {
      const empty = document.createElement('div');
      empty.style.color = 'var(--ink-dim)';
      empty.style.padding = '16px';
      empty.textContent = '아직 프로젝트가 없어요';
      this.list.appendChild(empty);
      return;
    }
    for (const p of projects) {
      this.list.appendChild(this.buildRow(p, p.id === currentId));
    }
  }

  private buildRow(p: ProjectMeta, isCurrent: boolean): HTMLElement {
    const row = document.createElement('div');
    row.className = 'font-row';

    const preview = document.createElement('div');
    preview.className = 'font-preview';
    preview.style.fontSize = '17px';
    preview.textContent = p.name + (isCurrent ? '  ●' : '');

    const sub = document.createElement('div');
    sub.className = 'font-sub';
    sub.textContent = `수정: ${formatDate(p.updatedAt)} · 생성: ${formatDate(p.createdAt)}`;

    const actions = document.createElement('div');
    actions.style.gridRow = '1 / span 2';
    actions.style.display = 'flex';
    actions.style.gap = '4px';

    const useBtn = document.createElement('button');
    useBtn.className = 'btn primary';
    useBtn.textContent = isCurrent ? '활성' : '열기';
    useBtn.disabled = isCurrent;
    useBtn.addEventListener('click', async () => {
      await switchProject(p.id);
      this.flash(`열림: ${p.name}`);
      this.refresh();
    });

    const renameBtn = document.createElement('button');
    renameBtn.className = 'btn ghost';
    renameBtn.textContent = '✎';
    renameBtn.title = '이름 변경';
    renameBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const next = prompt('새 이름', p.name);
      if (!next || next === p.name) return;
      await renameProject(p.id, next);
      this.refresh();
    });

    const dupBtn = document.createElement('button');
    dupBtn.className = 'btn ghost';
    dupBtn.textContent = '⎘';
    dupBtn.title = '복제';
    dupBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const c = await duplicateProject(p.id);
      if (c) this.flash(`복제됨: ${c.name}`);
      this.refresh();
    });

    const delBtn = document.createElement('button');
    delBtn.className = 'btn ghost';
    delBtn.textContent = '✕';
    delBtn.title = '삭제';
    delBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm(`정말 "${p.name}"를 삭제할까요? 되돌릴 수 없어요.`)) return;
      await deleteProject(p.id);
      this.refresh();
      this.flash('삭제됨');
    });

    actions.append(useBtn, renameBtn, dupBtn, delBtn);

    row.append(preview, sub, actions);
    return row;
  }

  private flash(msg: string): void {
    this.statusBar.textContent = `✅ ${msg}`;
    this.statusBar.style.color = 'var(--ok)';
  }
}

function formatDate(ms: number): string {
  if (!ms) return '-';
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
